/**
 * `GET /v1/me`: the signed-in user's profile, org, plan and meters. Declared once here
 * (firm-model spec §3.1 item 4); the server returns it, the plugin and the web panel
 * type against it. A new field is one edit in this file (type + schema), then
 * `npm run contract:sync`.
 */
import { z } from "zod";
import { expectParity } from "../parity.js";
import { CAPABILITIES, type Capability } from "./capabilities.js";

export type FirmRole = "admin" | "member";

export type Me = {
  userId: string;
  orgId: string;
  plan: string;
  isBeta: boolean;
  /** Plan feature gates (plan_features rows); an absent feature = enabled by default. */
  featureGates: Record<string, boolean>;
  /** null = unlimited (beta orgs / uncapped plans). */
  creditsRemaining: number | null;
  /** Credit grant for the current period (null = unlimited): the meter's denominator. */
  creditsTotal: number | null;
  /** Trial plans only (and never beta): when the AI gate closes. */
  trialEndsAt: string | null;
  /** Trial plans only (and never beta): when the trial began, for the panel's "Trial Days
   * Used" meter (2026-10-04). */
  trialStartsAt: string | null;
  /** Paid plans with an end-of-period cancel scheduled: when the plan ends. */
  planCancelsAt: string | null;
  /** Trial-only curated library-insert meter; null = unlimited (beta / paid plans).
   * Saved components are never counted. */
  libraryInserts: { used: number; limit: number; warnAt: number } | null;
  /** Full curated library (org predates the starter-catalog cutoff) vs starter examples
   * only. Optional on the wire: a pre-cutover server omits it (clients treat it as full). */
  fullCatalog?: boolean;
  /** The firm (org) name: `PROVISIONED_FIRM_NAME` until an admin names it (A8, Home's
   * Your Firm card). */
  orgName: string;
  /** Firm role (R2), for display: code checks `capabilities`, never this value. */
  role: FirmRole;
  /** What this user may do in the firm (spec §4.2); the panel shows controls from it. */
  capabilities: Capability[];
  /** A firm admin's email, for members ("Your firm's admin is X"); null for admins. */
  adminEmail: string | null;
  /** "disabled" = removed from the firm (R14): no AI calls, and sign-in says so. */
  status: "active" | "disabled";
  orgStatus: "active" | "closed";
  /** Paid plans: when the current billing period ends (the next renewal); null otherwise. */
  renewsAt: string | null;
  /** When the credit pool next resets (monthly plans); null for the trial pot or unlimited. */
  creditsResetAt: string | null;
  /** True once this user has loaded SlideBrain inside PowerPoint. */
  pluginSignedIn: boolean;
  /** Seats (firm-model spec §7, R20): what the firm pays for and what holds a seat. */
  seats: SeatSummary;
  /** Paddle couldn't collect the last payment (spec §10.2 Home, "payment failed"). */
  paymentFailed: boolean;
  /** The firm standards version (firm-model spec §3.2, §6.2): bumps on every save, so
   * clients refetch standards when it moves. 0 = the firm has never saved standards. */
  firmVersion: number;
};

export type SeatSummary = {
  /** Seats paid for (Individual and trial: 1). */
  count: number;
  /** The plan sells seats (Team): members, invites and the pool scale with them. */
  perSeat: boolean;
  /** Active members (each holds a seat). */
  members: number;
  /** Pending, unexpired invites (each holds a seat). */
  pendingInvites: number;
};

export const meSchema = z.object({
  userId: z.string(),
  orgId: z.string(),
  plan: z.string(),
  isBeta: z.boolean(),
  featureGates: z.record(z.boolean()),
  creditsRemaining: z.number().nullable(),
  creditsTotal: z.number().nullable(),
  trialEndsAt: z.string().nullable(),
  planCancelsAt: z.string().nullable(),
  libraryInserts: z.object({ used: z.number(), limit: z.number(), warnAt: z.number() }).nullable(),
  fullCatalog: z.boolean().optional(),
  orgName: z.string(),
  role: z.enum(["admin", "member"]),
  capabilities: z.array(z.enum(CAPABILITIES as [Capability, ...Capability[]])),
  adminEmail: z.string().nullable(),
  status: z.enum(["active", "disabled"]),
  orgStatus: z.enum(["active", "closed"]),
  renewsAt: z.string().nullable(),
  creditsResetAt: z.string().nullable(),
  pluginSignedIn: z.boolean(),
  // Defaults cover the minutes a new panel can be live before the API that sends these
  // (they deploy separately); an older API means a firm of one on a non-seat plan.
  seats: z
    .object({
      count: z.number().int(),
      perSeat: z.boolean(),
      members: z.number().int(),
      pendingInvites: z.number().int(),
    })
    .default({ count: 1, perSeat: false, members: 1, pendingInvites: 0 }),
  paymentFailed: z.boolean().default(false),
  // Same deploy-skew default: an API from before firm standards (A3) reports none.
  firmVersion: z.number().int().default(0),
  // Same again: an API from before 2026-10-04 sends no trial start (no trial meter then).
  trialStartsAt: z.string().nullable().default(null),
});

expectParity<z.infer<typeof meSchema>, Me>(true);

/** The name sign-up gives a new firm, until an admin renames it (Home's "Name your firm"). */
export const PROVISIONED_FIRM_NAME = "Personal";

/** `PUT /v1/firm/name` (`manage_members`): rename the firm. Trimmed, 1 to 80 characters. */
export type FirmNameRequest = { name: string };

export const firmNameRequestSchema = z.object({ name: z.string().trim().min(1).max(80) });

expectParity<z.infer<typeof firmNameRequestSchema>, FirmNameRequest>(true);
